// @flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import locationMarkerTheme from './LocationMarker.scss';
import BorderColor from 'common/components/core/colors/border.scss';
import Div from 'common/components/core/wrappers/Div';

export const ACTIVATE_ON = {
    EXTERNAL: 'EXTERNAL',
    HOVER: 'HOVER',
    SELFHOVER: 'SELFHOVER',
    SELFHOVERSELECT: 'SELFHOVERSELECT',
    HOVERSELECT: 'HOVERSELECT'
};

export const ALIGNMENT = {
    Default: 'DEFAULT',
    Center: 'CENTER',
    LeftBottom: 'LEFTBOTTOM'
};

const DefaultBackground = ({ withArrow, colorCode, theme }) => {
    let shapeStyle = {};
    let arrowStyle = {};
    if (Array.isArray(colorCode)) {
        shapeStyle = {
            background: `linear-gradient(135deg, ${colorCode[0]} 40%, ${colorCode[1]} 60%)`,
            color: 'black'
        };
        arrowStyle = {
            background: `linear-gradient(45deg, ${colorCode[0]} 40%, ${colorCode[1]} 60%)`,
            color: 'black'
        };
    }
    return (
        <span className={theme.background}>
            <span className={classnames(theme.shape)} style={shapeStyle} />
            {withArrow ? (
                <span className={classnames(theme.arrow)} style={arrowStyle} />
            ) : (
                []
            )}
            {withArrow ? (
                <span className={classnames(theme.arrowBorder)} />
            ) : (
                []
            )}
            <span className={classnames(theme.shapeBorder)} />
            <span className={classnames(theme.shadow)} />
        </span>
    );
};
const DefaultIcon = ({ markerIcon }) => {
    return <span>{markerIcon}</span>;
};

const DefaultPopupOuter = ({ theme, onMouseEnter, onMouseLeave, children }) => {
    return (
        <Div key={'markerPopup'} className={theme.popup}>
            <div
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={theme.popupInnerWrap}
            >
                {children}
            </div>
            <div className={theme.popupArrow} />
        </Div>
    );
};
const defaults = {
    Background: DefaultBackground,
    PopupOuter: DefaultPopupOuter,
    Icon: DefaultIcon,
    defaultTheme: locationMarkerTheme
};

const locationMarkerFactory = ({
    Icon = defaults.Icon,
    Background = defaults.Background,
    PopupInner,
    PopupOuter = defaults.PopupOuter,
    options = {}
}) => {
    const {
        customTheme: customTheme,
        defaultTheme: defaultTheme,
        themed = true
    } = {
        ...defaults,
        ...options
    };

    type State = {
        _targetNode: ?HTMLDivElement
    };

    class LocationMarkerComponent extends React.PureComponent<Props, State> {
        static defaultProps = {
            showPopupOn: ACTIVATE_ON.EXTERNAL,
            withArrow: true,
            markerAlignment: ALIGNMENT.Default,
            withBorder: false,
            markerIcon: null,
            sideLength: 12,
            height: 30,
            width: 26
        };

        state = {
            _targetNode: null
        };

        componentDidMount() {
            //using state rather than class variable because
            // we need to re-render the marker once the targetNode
            // is available for the showing the popup in case when the marker is rendered due to hover
            this.setState({
                _targetNode: this.markerRef.current
            });
        }

        markerRef = React.createRef<HTMLDivElement>();

        getCenteredMarkerStyle(height) {
            return {
                position: 'absolute',
                width: height,
                height,
                left: -height / 2,
                top: -height / 2
            };
        }

        getBottomMiddleMarkerStyle(height, width) {
            return {
                position: 'absolute',
                width,
                height,
                left: -width / 2,
                top: -height
            };
        }

        getLeftBottomMarkerStyle(height, width) {
            return {
                position: 'absolute',
                width,
                height,
                left: 0,
                top: -height
            };
        }

        getMarkerStyle({
            height,
            width,
            overrideStyle,
            sideLength,
            leftBottomMarker,
            centeredMarker
        }) {
            const wrapStyle = this.getBottomMiddleMarkerStyle(height, width);
            if (overrideStyle) {
                return overrideStyle;
            } else if (centeredMarker) {
                return this.getCenteredMarkerStyle(sideLength);
            } else if (leftBottomMarker) {
                return this.getLeftBottomMarkerStyle(height, width);
            }
            return wrapStyle;
        }

        getTriggerMouseEvents = () => {
            const { showPopupOn, isSelected, isPopupOpen } = this.props;
            if (showPopupOn === ACTIVATE_ON.SELFHOVERSELECT) {
                return isSelected;
            } else {
                return isPopupOpen;
            }
        };
        onPopupMouseEnter = () => {
            const {
                dangerouslyDisablePopupHover,
                $onMouseAllow,
                onPopupMouseEnter
            } = this.props;
            onPopupMouseEnter?.();
            const triggerMouseEvent = this.getTriggerMouseEvents();
            if (triggerMouseEvent && dangerouslyDisablePopupHover) {
                $onMouseAllow(false);
            }
        };

        onPopupMouseLeave = (forceTriggerMouseEvent = false) => {
            const {
                dangerouslyDisablePopupHover,
                $onMouseAllow,
                onPopupMouseLeave
            } = this.props;
            onPopupMouseLeave?.();
            const triggerMouseEvent = this.getTriggerMouseEvents();
            if (
                (triggerMouseEvent || forceTriggerMouseEvent) &&
                dangerouslyDisablePopupHover
            ) {
                $onMouseAllow(true);
            }
        };

        onContextMenu = e => {
            const { onContextMenu } = this.props;
            if (e) {
                e.preventDefault();
                e.stopPropagation();
                onContextMenu && onContextMenu(e);
            }
        };

        render() {
            const {
                isContextMenuOpen,
                $hover,
                theme,
                isSelected,
                isHovered,
                isArchived,
                color,
                borderColor,
                onClick,
                overrideStyle,
                className,
                showPopupOn,
                isPopupOpen,
                markerAlignment,
                withBorder,
                activateMarkerOn,
                isRotated,
                isHexagon,
                height,
                width,
                sideLength,
                onMouseEnter,
                onMouseLeave,
                isMarkerDisabled,
                'data-test-id': dataTestId
            } = this.props;
            const { _targetNode } = this.state;
            const { onContextMenu, ...otherProps } = this.props; // eslint-disable-line no-unused-vars
            var activateHover = false;
            switch (activateMarkerOn) {
                case ACTIVATE_ON.SELFHOVER:
                    activateHover = $hover;
                    break;
                case ACTIVATE_ON.HOVER:
                    activateHover = isHovered;
                    break;
                default:
                    activateHover = isHovered || $hover;
            }

            let showPopup = false;
            switch (showPopupOn) {
                case ACTIVATE_ON.EXTERNAL:
                    showPopup = isPopupOpen;
                    break;
                case ACTIVATE_ON.SELFHOVER:
                    showPopup = $hover || isPopupOpen;
                    break;
                case ACTIVATE_ON.HOVER:
                    showPopup = isHovered || isPopupOpen;
                    break;
                case ACTIVATE_ON.SELFHOVERSELECT:
                    showPopup = $hover || isPopupOpen || isSelected;
                    break;
                default:
                    showPopup = false;
                    break;
            }
            if (isContextMenuOpen) {
                showPopup = false;
            }

            const centeredMarker = markerAlignment === ALIGNMENT.Center;
            const leftBottomMarker = markerAlignment === ALIGNMENT.LeftBottom;

            const wrapClassName = classnames(
                theme.locationMarkerWrap,
                className,
                {
                    [theme[color]]: !Array.isArray(color),
                    [theme.markerHover]: activateHover,
                    [theme.archiveMarker]: isArchived,
                    [theme.selectedMarker]: isSelected,
                    [theme.centeredMarker]: centeredMarker,
                    [theme.leftBottomMarker]: leftBottomMarker,
                    [theme.withBorder]: withBorder,
                    [theme.isPopupOpen]: showPopup,
                    [theme.isRotated]: isRotated,
                    [theme.isHexagon]: isHexagon,
                    [theme.disabledMarker]: isMarkerDisabled
                }
            );

            const iconClassName = classnames(
                theme.icon,
                BorderColor[borderColor]
            );
            const wrapStyle = this.getMarkerStyle({
                height,
                width,
                sideLength,
                overrideStyle,
                centeredMarker,
                leftBottomMarker
            });

            return (
                <div
                    data-test-id={dataTestId}
                    ref={this.markerRef}
                    className={wrapClassName}
                    style={wrapStyle}
                    onClick={onClick}
                    onMouseEnter={onMouseEnter ? onMouseEnter : null}
                    onMouseLeave={onMouseLeave ? onMouseLeave : null}
                    onContextMenu={this.onContextMenu}
                >
                    <span className={iconClassName}>
                        <Icon {...otherProps} />
                    </span>
                    <Background {...otherProps} />
                    {_targetNode && PopupInner && showPopup ? (
                        <PopupOuter
                            {...otherProps}
                            theme={theme}
                            onMouseEnter={this.onPopupMouseEnter}
                            onMouseLeave={this.onPopupMouseLeave}
                            targetNode={_targetNode}
                        >
                            <PopupInner {...this.props} />
                        </PopupOuter>
                    ) : null}
                </div>
            );
        }
    }

    const themedLocationMarker = themed
        ? themr('LocationMarker', defaultTheme)(LocationMarkerComponent)
        : LocationMarkerComponent;
    return customTheme && themed
        ? themr('LocationMarkerThemed', customTheme)(themedLocationMarker)
        : themedLocationMarker;
};
const LocationMarker = locationMarkerFactory({
    Background: DefaultBackground,
    Icon: DefaultIcon
}); //default factory, no options
const UnthemedLocationMarker = locationMarkerFactory({
    Background: DefaultBackground,
    Icon: DefaultIcon,
    options: { themed: false }
}); //default factory, no options

export default LocationMarker;
export {
    LocationMarker,
    UnthemedLocationMarker,
    locationMarkerFactory,
    DefaultBackground as Background
};
